import React, { Fragment, useEffect } from "react";
import { func } from "prop-types";
import { Button, ButtonGroup, Form, Stack } from "react-bootstrap";
import Slider from "rc-slider";
import { useFormik } from "formik";
import { StarRating } from "@components";
import { useSelector } from "react-redux";
import styles from "../styles/Filters.module.css";
import { Filter } from "./Filter";
import { useSearchParams } from "react-router-dom";
import { searchParamsToObject } from "@helpers";
import { defaultFilters } from "../Data";
import { useExplore } from "@hooks";

const Filters = ({ hideFilterUI }) => {
  // state

  // variables
  const priceData = [
    {
      id: "free",
      name: "IsFree",
      label: "Free",
    },
    {
      id: "sale",
      name: "IsDiscount",
      label: "On Sale",
    },
  ];
  const ratingData = [0, 5, 4, 3, 2];
  let categoryData = [];
  let levelData = [];
  let languageData = [];

  // logics before hooks

  // hooks
  const [searchParams] = useSearchParams();
  const { allCategories } = useSelector((state) => state.category);
  const { getAllCourseLevel } = useSelector((state) => state.courseLevel);
  const { getAllAudioLanguage } = useSelector((state) => state.audioLanguage);
  const { filter, resetFilters } = useExplore();

  const { handleChange, setFieldValue, resetForm, handleSubmit, values } =
    useFormik({
      initialValues: defaultFilters,
      onSubmit: filter,
    });

  // logics
  const applyFilters = () => {
    hideFilterUI();
    handleSubmit();
  };

  const clearFilters = () => {
    resetForm({ values: defaultFilters });
    resetFilters(["Keyword", "Orderby", "PageSize", "PageNumber"]);
  };

  for (const category of allCategories.data ?? []) {
    categoryData = [...categoryData, ...(category.subCategories ?? [])];
  }

  levelData = getAllCourseLevel.data ?? [];

  languageData = getAllAudioLanguage.data ?? [];

  const priceFilterCounter = [
    "IsFree",
    "IsDiscount",
    "MinPrice",
    "MaxPrice",
  ].reduce(
    (prev, current) =>
      (prev += defaultFilters[current] !== values[current] ? 1 : 0),
    0
  );
  const ratingFilterCounter = ratingData.includes(values.Rate) ? 1 : 0;
  const categoryFilterCounter = values.CategoryId.length;
  const levelFilterCounter = values.LevelIds.length;
  const languageFilterCounter = values.AudioLanguageIds.length;

  const unAppliedFilterCounter =
    priceFilterCounter +
    ratingFilterCounter +
    categoryFilterCounter +
    levelFilterCounter +
    languageFilterCounter;

  // UI partials
  const price = (
    <Filter
      header={"Price"}
      counter={priceFilterCounter}
      body={
        <Fragment>
          {priceData.map((price) => (
            <Form.Check
              key={price.id}
              id={price.id}
              type="checkbox"
              name={price.name}
              label={price.label}
              checked={values[price.name]}
              onChange={() => setFieldValue(price.name, !values[price.name])}
            />
          ))}
          {values.IsDiscount || !values.IsFree ? (
            <div className="pt-2 border-top">
              <Form.Label>
                From{" "}
                <strong className="text-danger">{values.MinPrice} $</strong> to{" "}
                <strong className="text-danger">{values.MaxPrice} $</strong>
              </Form.Label>
              <div className="px-2">
                <Slider
                  range
                  min={defaultFilters.MinPrice}
                  max={defaultFilters.MaxPrice}
                  step={defaultFilters.MaxPrice / 10}
                  pushable={defaultFilters.MaxPrice / 10}
                  value={[values.MinPrice, values.MaxPrice]}
                  allowCross={false}
                  onChange={(value) => {
                    setFieldValue("MinPrice", value[0]);
                    setFieldValue("MaxPrice", value[1]);
                  }}
                  railStyle={{ height: "6px" }}
                  trackStyle={{ backgroundColor: "#ed2a26", height: "6px" }}
                  handleStyle={{
                    cursor: "pointer",
                    borderColor: "#ffecec",
                    backgroundColor: "#ed2a26",
                    width: "16px",
                    height: "16px",
                  }}
                />
              </div>
            </div>
          ) : null}
        </Fragment>
      }
    />
  );

  const rating = (
    <Filter
      header={"Rating"}
      counter={ratingFilterCounter}
      body={
        <Fragment>
          {ratingData.map((rating) => (
            <Form.Check
              className="d-flex align-items-center"
              key={`above${rating}`}
              id={`above${rating}`}
              type="radio"
              name={"Rate"}
              label={
                rating === 0 ? (
                  <span style={{ whiteSpace: "nowrap" }}>All Rating</span>
                ) : (
                  <span>
                    <StarRating numRating={rating} className="mr-2" />
                    <span style={{ whiteSpace: "nowrap" }}>
                      {rating}.0 & Up
                    </span>
                  </span>
                )
              }
              checked={values.Rate === rating}
              value={rating}
              onChange={() => setFieldValue("Rate", rating)}
            />
          ))}
        </Fragment>
      }
    />
  );

  const categories = (
    <Filter
      header={"Category"}
      counter={categoryFilterCounter}
      body={
        <Fragment>
          {categoryData.map((category) => (
            <Form.Check
              id={category.id}
              type="checkbox"
              name="CategoryId"
              key={category.id}
              label={category.name}
              value={category.id}
              checked={values.CategoryId.includes(category.id)}
              onChange={handleChange}
            />
          ))}
        </Fragment>
      }
    />
  );

  const levels = (
    <Filter
      header={"Level"}
      counter={levelFilterCounter}
      body={
        <Fragment>
          {levelData.map((level) => (
            <Form.Check
              id={level.id}
              type="checkbox"
              name="LevelIds"
              key={level.id}
              label={level.name}
              value={level.id}
              checked={values.LevelIds.includes(level.id)}
              onChange={handleChange}
            />
          ))}
        </Fragment>
      }
    />
  );

  const languages = (
    <Filter
      header={"Language"}
      counter={languageFilterCounter}
      body={
        <Fragment>
          {languageData.map((language) => (
            <Form.Check
              id={language.id}
              type="checkbox"
              name="AudioLanguageIds"
              key={language.id}
              label={language.name}
              value={language.id}
              checked={values.AudioLanguageIds.includes(language.id)}
              onChange={handleChange}
            />
          ))}
        </Fragment>
      }
    />
  );

  // effects
  useEffect(() => {
    const [currentFilters] = searchParamsToObject(searchParams, defaultFilters);
    resetForm({ values: currentFilters });
  }, [searchParams]);

  useEffect(() => {
    if (!values.IsDiscount && values.IsFree) {
      setFieldValue("MinPrice", defaultFilters.MinPrice);
      setFieldValue("MaxPrice", defaultFilters.MaxPrice);
    }
  }, [values.IsFree, values.IsDiscount]);

  // render
  return (
    <Form>
      <Stack className={styles.filterBody}>
        <ButtonGroup>
          <Button
            className="rounded-0"
            onClick={applyFilters}
            disabled={!unAppliedFilterCounter}
          >
            Apply ({unAppliedFilterCounter})
          </Button>
          <Button
            variant="light"
            onClick={clearFilters}
            disabled={!unAppliedFilterCounter}
          >
            Clear
          </Button>
        </ButtonGroup>
        <div>
          {price}
          {rating}
          {categories}
          {levels}
          {languages}
        </div>
      </Stack>
    </Form>
  );
};

/* required: display name */
Filters.displayName = "Filters";

/* required: props types */
Filters.propTypes = {
  hideFilterUI: func,
};

/* required: default props */
Filters.defaultProps = {
  hideFilterUI: () => {},
};

export { Filters };
